import { API_BASE_URL } from '../config/api';

/**
 * @typedef {{
 *   memberId: string,
 *   parentMemberId: string | null,
 *   linkedSubMemberId: string | null,
 *   groupId: string,
 *   fullName: string,
 *   email: string,
 *   phoneNumber: string,
 *   profilePictureUrl: string | null,
 *   role: string,
 *   token: string,
 *   expiresAt: string,
 * }} MemberLoginData
 */

/**
 * @param {unknown} body
 * @returns {string}
 */
function readErrorMessage(body) {
    if (body && typeof body === 'object') {
        const o = /** @type {Record<string, unknown>} */ (body);
        const m = o.message ?? o.Message ?? o.title ?? o.Title;
        if (typeof m === 'string' && m.trim()) return m;
    }
    return 'Giriş başarısız';
}

/**
 * @param {unknown} raw
 * @returns {Record<string, unknown> | null}
 */
function asObject(raw) {
    return raw && typeof raw === 'object' && !Array.isArray(raw)
        ? /** @type {Record<string, unknown>} */ (raw)
        : null;
}

/**
 * @param {Record<string, unknown>} r
 * @param {readonly string[]} keys
 */
function pickStr(r, keys) {
    for (let i = 0; i < keys.length; i += 1) {
        const v = r[keys[i]];
        if (v != null && String(v).trim()) return String(v).trim();
    }
    return '';
}

/**
 * API: `data.session` (veya düz `data`) içindeki alanları MemberLoginData'ya çevirir.
 * @param {Record<string, unknown>} r
 * @param {string} identifierFallback
 * @returns {MemberLoginData | null}
 */
function mapSessionToMemberLoginData(r, identifierFallback) {
    const token = pickStr(r, [
        'token',
        'Token',
        'accessToken',
        'AccessToken',
    ]);
    if (!token) return null;

    const email =
        pickStr(r, ['email', 'Email', 'userName', 'UserName']) ||
        identifierFallback;

    const fullName = pickStr(r, [
        'fullName',
        'FullName',
        'name',
        'Name',
    ]);

    const memberId = pickStr(r, ['memberId', 'MemberId']);
    const parentPick = pickStr(r, ['parentMemberId', 'ParentMemberId']);
    const linkedPick = pickStr(r, ['linkedSubMemberId', 'LinkedSubMemberId']);
    const groupId = pickStr(r, ['groupId', 'GroupId']);
    const phoneNumber = pickStr(r, ['phoneNumber', 'PhoneNumber', 'phone']);
    const rawPic = r.profilePictureUrl ?? r.ProfilePictureUrl;
    const profilePictureUrl =
        typeof rawPic === 'string' && rawPic.trim() ? rawPic.trim() : null;
    const role = pickStr(r, ['role', 'Role']);
    const expiresAt = pickStr(r, ['expiresAt', 'ExpiresAt']);

    return {
        memberId,
        parentMemberId: parentPick || null,
        linkedSubMemberId: linkedPick || null,
        groupId,
        email,
        fullName,
        phoneNumber,
        profilePictureUrl,
        role,
        token,
        expiresAt,
    };
}

/**
 * Başarılı yanıt gövdesinden üye oturum nesnesini çıkarır.
 * - Yeni: `{ data: { otpRequired, session: { token, ... }, otpChallenge } }`
 * - Eski: `{ data: { token, email, ... } }` (düz)
 *
 * @param {unknown} body
 * @param {string} identifierFallback
 * @returns {MemberLoginData}
 */
function extractMemberLoginFromSuccessBody(body, identifierFallback) {
    const root = asObject(body);
    if (!root) {
        throw new Error(readErrorMessage(body));
    }

    const dataLayer = asObject(root.data) ?? asObject(root.Data);
    if (!dataLayer) {
        throw new Error(readErrorMessage(body));
    }

    const otpRequired =
        dataLayer.otpRequired === true ||
        /** @type {{ OtpRequired?: boolean }} */ (dataLayer).OtpRequired ===
            true;

    const sessionObj =
        asObject(dataLayer.session) ?? asObject(dataLayer.Session);

    /** Düz gövde: token doğrudan `data` üzerinde */
    const flatHasToken =
        dataLayer.token != null ||
        dataLayer.Token != null ||
        dataLayer.accessToken != null ||
        dataLayer.AccessToken != null;

    let source = sessionObj;
    if (!source && flatHasToken) {
        source = dataLayer;
    }

    if (otpRequired && !source) {
        throw new Error(
            'Bu hesap için ek doğrulama (OTP) gerekiyor. Lütfen yönergeleri tamamlayın.',
        );
    }

    if (!source) {
        throw new Error(readErrorMessage(body));
    }

    const member = mapSessionToMemberLoginData(source, identifierFallback);
    if (!member) {
        throw new Error(readErrorMessage(body));
    }

    return member;
}

/**
 * Oturumda saklanacak kullanıcı nesnesi (UI + sonraki istekler için token).
 * @param {MemberLoginData} data
 */
export function sessionUserFromLoginData(data) {
    return {
        email: data.email,
        name: data.fullName,
        token: data.token,
        expiresAt: data.expiresAt,
        memberId: data.memberId,
        parentMemberId: data.parentMemberId,
        linkedSubMemberId: data.linkedSubMemberId,
        groupId: data.groupId,
        phoneNumber: data.phoneNumber,
        profilePictureUrl: data.profilePictureUrl,
        role: data.role,
        simulatedSession: false,
    };
}

/**
 * @param {string} identifier E-posta veya kullanıcı adı
 * @param {string} password
 * @returns {Promise<ReturnType<typeof sessionUserFromLoginData>>}
 */
export async function loginMember(identifier, password) {
    const url = `${API_BASE_URL}/members/Auth/Login`;
    const trimmedId = identifier.trim();

    /** @type {RequestCredentials | undefined} */
    let credentials;
    if (process.env.REACT_APP_API_LOGIN_CREDENTIALS === 'include') {
        credentials = 'include';
    }

    const res = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        ...(credentials ? { credentials } : {}),
        body: JSON.stringify({
            identifier: trimmedId,
            password,
        }),
    });

    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error('Sunucu yanıtı okunamadı');
    }

    if (!res.ok) {
        throw new Error(readErrorMessage(body));
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (
        /** @type {{ success?: boolean }} */ (obj).success === false ||
        /** @type {{ Success?: boolean }} */ (obj).Success === false
    ) {
        throw new Error(readErrorMessage(body));
    }

    const member = extractMemberLoginFromSuccessBody(body, trimmedId);
    return sessionUserFromLoginData(member);
}

/**
 * @param {Response} res
 * @param {string} failLabel
 * @returns {Promise<unknown>}
 */
async function parseMemberAuthJsonResponse(res, failLabel) {
    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error('Sunucu yanıtı okunamadı');
    }

    if (!res.ok) {
        throw new Error(readErrorMessage(body) || `${failLabel} (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (
        /** @type {{ success?: boolean }} */ (obj).success === false ||
        /** @type {{ Success?: boolean }} */ (obj).Success === false
    ) {
        throw new Error(readErrorMessage(body));
    }

    return body;
}

/**
 * @param {unknown} body
 * @returns {string}
 */
function readSuccessMessage(body) {
    if (body && typeof body === 'object') {
        const m = /** @type {{ message?: string }} */ (body).message;
        if (typeof m === 'string' && m.trim()) return m.trim();
    }
    return 'Şifre başarıyla güncellendi.';
}

/**
 * `POST /members/Auth/ChangePassword` (Bearer).
 * @param {string} accessToken
 * @param {{ currentPassword: string, newPassword: string, confirmPassword: string }} payload
 * @returns {Promise<string>} Başarı mesajı
 */
export async function changeMemberPassword(accessToken, payload) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }

    const url = `${API_BASE_URL}/members/Auth/ChangePassword`;
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
            currentPassword: payload.currentPassword,
            newPassword: payload.newPassword,
            confirmPassword: payload.confirmPassword,
        }),
    });

    const body = await parseMemberAuthJsonResponse(
        res,
        'Şifre güncellenemedi',
    );
    return readSuccessMessage(body);
}
